import React, { useState, useEffect, useRef } from 'react'
import './PushNotification.scss'
import { NotificationXModel } from 'BoostXpressAdmin/Model/notificationXModel'
import { userXModel } from 'BoostXpressAdmin/Model/userXModel'
import { notificationBXService } from 'BoostXpressAdmin/Services/notificationBXService'
import { userBXService } from 'BoostXpressAdmin/Services/userBXService'
import { handleError, handleSuccess } from 'Helpers'
import { ToastContainer } from 'react-toastify'
import { HorizontalLoader } from 'Components'

const PushNotification: React.FC = () => {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [audience, setAudience] = useState('ALL')
  const [delivery, setDelivery] = useState('')
  const [targetUserResourceId, setTargetUserResourceId] = useState('')

  const [targetUserResourceIds, setTargetUserResourceIds] = useState<string[]>()
  const [targetUserResourceIdsInput, setTargetUserResourceIdsInput] = useState('')

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<userXModel[]>([])
  const [selectedUsers, setSelectedUsers] = useState<userXModel[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [notifications, setNotifications] = useState<NotificationXModel[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)

  // Filter states
  const [targetSegment, setTargetSegment] = useState('')

  let cancelled = false

  const loadNotifications = async () => {
    try {
      setIsLoading(true)
      const res = await notificationBXService.index({
        page: 1,
        limit: 10,
        sortOrder: 'DESC',
      })
      if (cancelled) return
      setNotifications(res.data.data ?? [])
    } catch (e) {
      if (!cancelled) {
        setNotifications([])
      }
    } finally {
      if (!cancelled) setIsLoading(false)
    }
  }
  useEffect(() => {
    loadNotifications()
    return () => {
      cancelled = true
    }
  }, [])

  // Debounced search for users
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([])
      setShowDropdown(false)
      return
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true)
        const res = await userBXService.index(1, 10, 'ASC', { search: searchQuery })
        setSearchResults(res.data.data ?? [])
        setShowDropdown(true)
      } catch (e) {
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelectUser = (user: userXModel) => {
    if (!selectedUsers.find((u) => u.resourceId === user.resourceId)) {
      setSelectedUsers([...selectedUsers, user])
      setTargetUserResourceId(
        [...selectedUsers.map((u) => u.resourceId), user.resourceId].join(','),
      )
    }
    setSearchQuery('')
    setShowDropdown(false)
  }

  const handleRemoveUser = (userResourceId: string) => {
    const updatedUsers = selectedUsers.filter((u) => u.resourceId !== userResourceId)
    setSelectedUsers(updatedUsers)
    setTargetUserResourceId(updatedUsers.map((u) => u.resourceId).join(','))
  }

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      handleError({ response: { data: { error: 'Please fill in both title and message' } } })
      return
    }

    if (audience === 'SINGLE_USER' && selectedUsers.length === 0) {
      handleError({ response: { data: { error: 'Please select at least one user' } } })
      return
    }

    setIsSending(true)

    const payload: any = {
      title,
      body: message,
      targetType: audience,
    }

    // Add filters if applicable
    if (audience === 'SEGMENT') {
      if (targetSegment) payload.targetSegment = targetSegment
    }
    try {
      if (audience === 'SINGLE_USER') {
        // Send notification to each selected user individually.
        for (const user of selectedUsers) {
          payload.targetUserResourceId = user.resourceId
          await notificationBXService.send(payload)
        }
      } else {
        if (targetUserResourceIds) payload.targetUserResourceIds = targetUserResourceIds

        await notificationBXService.send(payload)
      }

      loadNotifications()

      // Clear form after successful send
      setTitle('')
      setMessage('')
      setTargetUserResourceId('')
      setTargetUserResourceIds(undefined)
      setTargetUserResourceIdsInput('')
      setSelectedUsers([])
      setSearchQuery('')
      setTargetSegment('')

      handleSuccess('Notification sent successfully!')
      setIsSending(false)
    } catch (e) {
      handleError(e as { response?: { data?: { error: string } } })
      setIsSending(false)
    }
  }

  const handleTargetUserResourceIdsChange = (resourceIds: string) => {
    let rIDs = resourceIds

    // If values are space-separated without commas, insert the missing commas.
    const trimmed = rIDs.trim()
    if (trimmed && !trimmed.includes(',') && /\s/.test(trimmed)) {
      rIDs = trimmed.split(/\s+/).filter(Boolean).join(',')
    }

    setTargetUserResourceIdsInput(rIDs)
    setTargetUserResourceIds(
      rIDs
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    )
  }

  return (
    <div className='push-notification-container d-flex flex-column align-items-start w-100'>
      <div className='d-flex align-items-center justify-content-between w-100'>
        <p className='push-notification-title mb-0'>Compose and send targeted push notifications</p>
      </div>
      <div className='row w-100'>
        {/* Compose Notification */}
        <div className='col-md-6'>
          <div className='compose-section w-100'>
            <p className='compose-title mb-0'>Compose Notification</p>
            <div className='compose-form bg-white border rounded d-flex flex-column  mt-2'>
              {/* Title */}
              <div className='w-100'>
                <label className='push-label form-label'>Title</label>
                <input
                  type='text'
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder='e.g Total Energies Lekki'
                  className='push-input form-control'
                />
              </div>

              {/* Message Body */}
              <div className='w-100'>
                <label className='push-label form-label'>Message Body</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder='write your notification message here'
                  rows={4}
                  className='push-textarea form-control'
                />
              </div>

              {/* Target Audience */}
              <div className='w-100'>
                <label className='push-label form-label'>Target Audience</label>
                <div className='position-relative'>
                  <select
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    className='push-select form-select'
                  >
                    <option value='ALL'>All Users</option>
                    <option value='SINGLE_USER'>User</option>
                    <option value='MULTIPLE_USERS'>Multiple Users</option>
                    <option value='SEGMENT'>Segments</option>
                  </select>
                  <div className='push-arrow'>
                    <svg width='18' height='18' viewBox='0 0 18 18' fill='none'>
                      <path
                        d='M4.5 6.75L9 11.25L13.5 6.75'
                        stroke='#82858A'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Audience Filters - Show when All Users is selected */}
              {audience === 'SEGMENT' && (
                <>
                  <div className='w-100 mt-0'>
                    <label className='push-label form-label'>Segment</label>
                    <div className='position-relative'>
                      <select
                        value={targetSegment}
                        onChange={(e) => setTargetSegment(e.target.value)}
                        className='push-select form-select'
                      >
                        <option value=''>All Groups</option>
                        <option value='unfunded_dormant'>Unfunded Dormant</option>
                        <option value='depositors'>Depositors</option>
                        <option value='trialists'>Trialists</option>
                        <option value='micro_transactors'>Micro Transactors</option>
                        <option value='mid_tier'>Mid Tier</option>
                        <option value='power_users'>Power Users</option>
                        <option value='recently_active'>Recently Active</option>
                        <option value='churned'>Churned</option>
                        <option value='voucher_users'>Voucher Users</option>
                        <option value='referrers'>Referrers</option>
                        <option value='phone_unverified'>Phone Unverified</option>
                      </select>
                      <div className='push-arrow'>
                        <svg width='18' height='18' viewBox='0 0 18 18' fill='none'>
                          <path
                            d='M4.5 6.75L9 11.25L13.5 6.75'
                            stroke='#82858A'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {audience === 'MULTIPLE_USERS' && (
                <div className='w-100 position-relative'>
                  <label className='push-label form-label'>
                    Target Users <small>(Seperate the IDs with comma)</small>
                  </label>
                  <textarea
                    value={targetUserResourceIdsInput}
                    onChange={(e) => handleTargetUserResourceIdsChange(e.target.value)}
                    placeholder='e.g 123456,789012'
                    className='push-textarea form-control'
                  />
                </div>
              )}

              {/* Target User Resource ID - Show only when User is selected */}
              {audience === 'SINGLE_USER' && (
                <div className='w-100 position-relative'>
                  <label className='push-label form-label'>Target Users</label>
                  <div className='user-multi-select-container' ref={dropdownRef}>
                    {/* Selected Users Tags */}
                    <div className='selected-users-tags'>
                      {selectedUsers.map((user) => (
                        <div key={user.resourceId} className='user-tag'>
                          <span className='user-tag-text'>
                            {user.firstName} {user.lastName}
                          </span>
                          <button
                            type='button'
                            className='user-tag-remove'
                            onClick={() => handleRemoveUser(user.resourceId)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    {/* Search Input */}
                    <input
                      type='text'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setShowDropdown(true)}
                      placeholder='Search users by name...'
                      className='push-input form-control user-search-input'
                    />
                    {/* Dropdown */}
                    {showDropdown && (searchResults.length > 0 || isSearching) && (
                      <div className='user-search-dropdown'>
                        {isSearching ? (
                          <div className='dropdown-item dropdown-loading'>Searching...</div>
                        ) : searchResults.length === 0 ? (
                          <div className='dropdown-item dropdown-no-results'>No users found</div>
                        ) : (
                          searchResults.map((user) => {
                            const isSelected = selectedUsers.find(
                              (u) => u.resourceId === user.resourceId,
                            )
                            return (
                              <div
                                key={user.resourceId}
                                className={`dropdown-item ${isSelected ? 'dropdown-item-disabled' : ''}`}
                                onClick={() => !isSelected && handleSelectUser(user)}
                              >
                                <div className='dropdown-item-content'>
                                  <span className='dropdown-item-name'>
                                    {user.firstName} {user.lastName}
                                  </span>
                                  <span className='dropdown-item-email'>{user.email}</span>
                                </div>
                                {isSelected && <span className='dropdown-item-selected'>✓</span>}
                              </div>
                            )
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Delivery */}
              <div className='w-100'>
                <label className='push-label form-label'>Delivery</label>
                <div className='position-relative'>
                  <select
                    value={delivery}
                    onChange={(e) => setDelivery(e.target.value)}
                    className='push-select form-select'
                  >
                    <option value=''>Select</option>
                    <option value='now'>Send Now</option>
                    <option value='schedule'>Schedule</option>
                  </select>
                  <div className='push-arrow'>
                    <svg width='18' height='18' viewBox='0 0 18 18' fill='none'>
                      <path
                        d='M4.5 6.75L9 11.25L13.5 6.75'
                        stroke='#82858A'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <button onClick={handleSend} className='push-send-btn' disabled={isSending}>
                {isSending ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </div>
        </div>

        {/* Recent Campaigns */}
        <div className='col-md-6'>
          <div className='campaigns-section w-100'>
            <p className='campaigns-title mb-0'>Recent Campaigns</p>
            <div className='campaigns-list bg-white border rounded d-flex flex-column mt-2'>
              {isLoading ? (
                <div className='campaign-item d-flex justify-content-center align-items-center p-4'>
                  <span>
                    <HorizontalLoader />
                  </span>
                </div>
              ) : notifications.length === 0 ? (
                <div className='campaign-item d-flex justify-content-center align-items-center p-4'>
                  <span>No notifications found</span>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.resourceId}
                    className='campaign-item d-flex justify-content-between align-items-start'
                  >
                    <div
                      className='d-flex flex-column'
                      style={{
                        gap: '8px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                      }}
                    >
                      <p
                        className='campaign-name mb-0'
                        style={{
                          maxWidth: '300px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {notification.title}
                      </p>
                      <p className='campaign-stats mb-0'>
                        Target: {notification.targetType} · Sent by:{' '}
                        {notification.sentBy?.firstName ?? ''} {notification.sentBy?.lastName ?? ''}
                      </p>
                      {notification.targetUser && (
                        <p className='campaign-stats mb-0'>
                          Recipient: {notification.targetUser?.firstName ?? ''}{' '}
                          {notification.targetUser?.lastName ?? ''}
                        </p>
                      )}
                      <p className='campaign-stats mb-0'>
                        {new Date(notification.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className='campaign-status-sent'>Sent</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  )
}

export default PushNotification
